/**
 * kleinBottlePipeline — Klein bottle raymarched in 3D.
 *
 * The Klein bottle is a non-orientable 4D surface that can be
 * parameterized in 3D using the figure-8 immersion:
 *   x = (a + cos(u/2) sin(v) - sin(u/2) sin(2v)) cos(u)
 *   y = (a + cos(u/2) sin(v) - sin(u/2) sin(2v)) sin(u)
 *   z = sin(u/2) sin(v) + cos(u/2) sin(2v)
 *
 * Render as a hollow surface with iridescent color depending on u/v.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface KleinBottlePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

// Klein bottle figure-8 immersion.
vec3 kleinPoint(vec2 uv) {
  float u = uv.x * 6.2831853;
  float v = uv.y * 6.2831853;
  float a = 2.0;
  float cu2 = cos(u / 2.0);
  float su2 = sin(u / 2.0);
  float cu = cos(u);
  float su = sin(u);
  float sv = sin(v);
  float s2v = sin(2.0 * v);
  float c2v = cos(2.0 * v);
  vec3 p;
  // The figure-8 immersion in 3D (Boy's surface variant).
  // Use the classic Klein bottle in 3D:
  float r = a + cu2 * sv - su2 * s2v;
  p.x = r * cu;
  p.y = r * su;
  p.z = su2 * sv + cu2 * s2v;
  // Scale down.
  return p * 0.5;
}

// Closest-point distance to the Klein bottle via sampling.
float sdKlein(vec3 p) {
  // Sample many (u, v) points and find minimum distance.
  float minDist = 1000.0;
  for (int i = 0; i < 32; i++) {
    float u = float(i) / 32.0;
    for (int j = 0; j < 16; j++) {
      float v = float(j) / 16.0;
      vec2 uv = vec2(u, v);
      vec3 q = kleinPoint(uv);
      float d = length(p - q);
      if (d < minDist) minDist = d;
    }
  }
  return minDist;
}

void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;
  float angle = uTime * 0.3;
  vec3 ro = vec3(2.5 * cos(angle), 0.5 * sin(uTime * 0.4), 2.5 * sin(angle));
  vec3 ta = vec3(0.0, 0.0, 0.0);
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  vec3 rd = normalize(p.x * cu + p.y * cv + 1.5 * cw);

  // Sample the Klein bottle for each pixel; find the closest point.
  // Use a 2D lookup based on screen position.
  vec2 uv = (gl_FragCoord.xy / uResolution.xy);
  // Animate the surface parameter.
  vec2 offset = vec2(uTime * 0.05, 0.0);
  vec2 uvA = mod(uv + offset, vec2(1.0));
  vec3 closest = kleinPoint(uvA);
  // Determine which (u, v) sample is closest via a 5x5 search.
  vec3 minP = closest;
  vec3 p3 = vec3(p, 0.0);
  float minD = length(p3 - closest);
  for (int du = -2; du <= 2; du++) {
    for (int dv = -2; dv <= 2; dv++) {
      vec2 uvN = mod(uvA + vec2(float(du) * 0.02, float(dv) * 0.02), vec2(1.0));
      vec3 q = kleinPoint(uvN);
      float d = length(p3 - q);
      if (d < minD) { minD = d; minP = q; }
    }
  }

  // Render as a soft glow around the closest point on the surface.
  float glow = exp(-minD * minD * 1000.0);
  // Color based on surface parameter u (position around the bottle).
  float hue = uvA.x;
  vec3 col = vec3(
    0.5 + 0.5 * sin(hue * 6.2831853 + 0.0),
    0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
    0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
  );
  // Background.
  vec3 bg = mix(vec3(0.02, 0.03, 0.08), vec3(0.06, 0.04, 0.12), vUV.y);
  vec3 outCol = bg + col * glow * (1.0 + bass * 0.5);
  // Audio brightness.
  outCol *= 1.0 + mid * 0.3;

  float vig = 1.0 - dot(p, p) * 0.3;
  outCol *= clamp(vig, 0.0, 1.0);
  outCol = (outCol * (2.51 * outCol + 0.03)) / (outCol * (2.43 * outCol + 0.59) + 0.14);
  outCol = pow(clamp(outCol, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(outCol, 1.0);
}
`;

export function computeKleinBottleBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeKleinBottleRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createKleinBottlePipeline(gl: WebGL2RenderingContext): KleinBottlePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawKleinBottle(
  gl: WebGL2RenderingContext,
  pipe: KleinBottlePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
